/**
 * Neues Passwort setzen (Phase C, Kapitel 3.3) — Ziel des Links aus der
 * Passwort-Reset-E-Mail (`?token=...`). Funktioniert ohne Login.
 * v1.4.0: auch Ziel der Einladungs-Mail (`&einladung=1` → Willkommens-Text).
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const isInvite = searchParams.get('einladung') === '1';
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 8) {
      setError('Neues Passwort: mindestens 8 Zeichen.');
      hapticError();
      return;
    }
    if (newPassword !== newPassword2) {
      setError('Passwörter stimmen nicht überein.');
      hapticError();
      return;
    }
    setBusy(true);
    try {
      await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        body: { token, newPassword },
      });
      hapticSuccess();
      setDone(true);
    } catch (err) {
      hapticError();
      const message = err instanceof ApiError ? err.message : 'Zurücksetzen fehlgeschlagen';
      setError(
        isInvite && err instanceof ApiError && err.status === 400 && /ungültig oder abgelaufen/.test(message)
          ? 'Dieser Einladungs-Link ist abgelaufen oder wurde schon benutzt. Bitte deine Führungskraft, dir eine neue Einladung zu schicken — oder nutze „Passwort vergessen“.'
          : message,
      );
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <div className="max-w-sm mx-auto mt-12 text-center space-y-4">
        <h2 className="text-2xl font-bold">Ungültiger Link</h2>
        <p className="text-sm text-slate-400">
          Dieser Link enthält kein Reset-Token. Fordere über „Passwort vergessen" einen
          neuen an.
        </p>
        <Link to="/passwort-vergessen" className="text-brand-light hover:text-brand underline">
          Neuen Link anfordern
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-sm mx-auto mt-12">
      <h2 className="text-2xl font-bold text-center mb-2">
        {isInvite ? 'Willkommen bei ShiftSync' : 'Neues Passwort setzen'}
      </h2>
      {isInvite && !done && (
        <p className="text-sm text-slate-400 text-center mb-6">
          Lege dein eigenes Passwort fest (mindestens 8 Zeichen). Danach meldest du dich mit deiner E-Mail-Adresse und
          diesem Passwort an.
        </p>
      )}
      {!isInvite && <div className="mb-4" />}
      {done ? (
        <div className="space-y-4 text-center">
          <p className="text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded px-3 py-2">
            {isInvite ? 'Passwort gespeichert.' : 'Passwort geändert.'} Du kannst dich jetzt anmelden.
          </p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full bg-brand hover:bg-brand-dark transition rounded py-2 font-medium"
          >
            Zum Login
          </button>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="newPassword" className="block text-sm text-slate-300 mb-1">
              Neues Passwort
            </label>
            <input
              id="newPassword"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 focus:outline-none focus:border-brand"
            />
          </div>
          <div>
            <label htmlFor="newPassword2" className="block text-sm text-slate-300 mb-1">
              Neues Passwort (wiederholen)
            </label>
            <input
              id="newPassword2"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword2}
              onChange={(e) => setNewPassword2(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 focus:outline-none focus:border-brand"
            />
          </div>
          {error && (
            <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded px-3 py-2">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            className="w-full bg-brand hover:bg-brand-dark transition rounded py-2 font-medium disabled:opacity-50"
          >
            {busy ? 'Speichere…' : isInvite ? 'Passwort festlegen' : 'Passwort setzen'}
          </button>
        </form>
      )}
    </div>
  );
}
